/* eslint-disable react-hooks/exhaustive-deps */
import { useContext, useState, useEffect, useCallback, useImperativeHandle, useRef, ForwardedRef } from 'react'
import { CarouselContext } from 'pure-react-carousel'

export interface CarouselHandle {
    goToSlide: (index: number) => void
    next: () => void
    prev: () => void
    getCurrentSlide: () => number
    stopAutoplay: () => void
    resumeAutoplay: () => void
    isAutoPlaying: boolean
}

interface UseCarouselProps {
    onSlideChange?: (index: number) => void
    initialSlide?: number
}

export const useCarousel = (
    ref?: ForwardedRef<CarouselHandle>,
    { onSlideChange, initialSlide = 0 }: UseCarouselProps = {},
) => {
    const carouselContext = useContext(CarouselContext)

    const [currentSlide, setCurrentSlide] = useState(carouselContext?.state?.currentSlide ?? initialSlide)
    const [isPlaying, setIsPlaying] = useState(carouselContext?.state?.isPlaying ?? false)

    // Store onSlideChange in a ref to avoid triggering re-renders
    const onSlideChangeRef = useRef(onSlideChange)
    useEffect(() => {
        onSlideChangeRef.current = onSlideChange
    }, [onSlideChange])

    // Memoized state update function to avoid unnecessary re-renders
    const onChange = useCallback(() => {
        if (!carouselContext) {
            return
        }

        const newSlide = carouselContext.state.currentSlide
        const newIsPlaying = carouselContext.state.isPlaying

        setCurrentSlide(prev => (prev !== newSlide ? newSlide : prev))
        setIsPlaying(prev => (prev !== newIsPlaying ? newIsPlaying : prev))
        if (onSlideChangeRef.current) {
            onSlideChangeRef.current(newSlide)
        }
    }, [carouselContext])

    // Subscribe to carousel state changes efficiently
    useEffect(() => {
        if (!carouselContext) {
            return
        }

        carouselContext.subscribe(onChange)
        return () => carouselContext.unsubscribe(onChange)
    }, [carouselContext, onChange])

    // Memoized functions to avoid unnecessary re-renders
    const goToSlide = useCallback(
        (index: number) => {
            carouselContext?.setStoreState({ currentSlide: index })
        },
        [carouselContext],
    )

    const next = useCallback(() => {
        if (!carouselContext) {
            return
        }
        const { currentSlide, totalSlides } = carouselContext.state
        goToSlide((currentSlide + 1) % totalSlides)
    }, [carouselContext, goToSlide])

    const prev = useCallback(() => {
        if (!carouselContext) {
            return
        }
        const { currentSlide, totalSlides } = carouselContext.state
        goToSlide((currentSlide - 1 + totalSlides) % totalSlides)
    }, [carouselContext, goToSlide])

    const stopAutoplay = useCallback(() => {
        carouselContext?.setStoreState({ isPlaying: false })
    }, [carouselContext])

    const resumeAutoplay = useCallback(() => {
        carouselContext?.setStoreState({ isPlaying: true })
    }, [carouselContext])

    useImperativeHandle(
        ref,
        () => ({
            goToSlide,
            next,
            prev,
            getCurrentSlide: () => currentSlide,
            stopAutoplay,
            resumeAutoplay,
            isAutoPlaying: isPlaying,
        }),
        [goToSlide, next, prev, currentSlide, stopAutoplay, resumeAutoplay, isPlaying],
    )

    return {
        currentSlide,
        goToSlide,
        next,
        prev,
        stopAutoplay,
        resumeAutoplay,
        isAutoPlaying: isPlaying,
    }
}
